import { AxiosError } from "axios";
import cx from "classnames";
import React from "react";
import { DropdownInput, IOption, Shimmer } from "component-library";

import { DEFAULT_CLIENT_AGENCY_ID, NO_CLIENT_AGENCY_ID } from "config/common";

import { INudge } from "models/suggestion";

import {
  GetAgencies,
  IAgencyPermission,
} from "shared/services/api/agents/agentsService";

import {
  getAgencyOption,
  getNudgeAgencyText,
  prepareText,
} from "components/SuggestionsViewPerformer/NudgeAccessPerformer/NudgeAccessPerformer.helpers";
import { IViewPerformerButtonProps } from "components/SuggestionsViewPerformer/SuggestionsViewPerformer";
import { IRoleSuggestionsViewTexts } from "components/SuggestionsViewPerformer/SuggestionsViewPerformer.helpers";

import "../SuggestionsViewPerformer.scss";

export interface INudgeAccessPerformerProps {
  texts: IRoleSuggestionsViewTexts;
  isRoleClosed: boolean;
  nudge?: INudge;
  renderTitle: () => React.ReactNode;
  renderButtons: (props: IViewPerformerButtonProps) => React.ReactNode;
  onNudge: (agencyId: string) => void;
  getAgencies: GetAgencies;
  handleError: (e: AxiosError) => void;
  isPrintView?: boolean;
  isRoleCast?: boolean | null;
}

export interface INudgeAccessPerformerState {
  isLoading: boolean;
  isEditMode: boolean;
  selectedAgentId: string;
  agencies: IAgencyPermission[];
}

export class NudgeAccessPerformer extends React.Component<
  INudgeAccessPerformerProps,
  INudgeAccessPerformerState
> {
  public requestCancellation = new AbortController();

  public readonly state: Readonly<INudgeAccessPerformerState> = {
    isLoading: !this.props.nudge && !this.props.isRoleClosed,
    isEditMode: !this.props.nudge,
    selectedAgentId: "",
    agencies: [],
  };

  public componentDidMount() {
    const { nudge, isRoleClosed } = this.props;

    if (!nudge && !isRoleClosed) {
      void this.getAgencyList();
    }
  }

  public componentWillUnmount() {
    this.requestCancellation.abort?.();
  }

  public render() {
    const {
      nudge,
      isRoleClosed,
      renderButtons,
      texts,
      isPrintView,
      isRoleCast,
    } = this.props;
    const { isEditMode, isLoading } = this.state;

    if ((!nudge && isRoleClosed) || isRoleCast) {
      return renderButtons({
        primary: {
          text: isRoleCast
            ? texts.makeSuggestionIsRoleCastNudgeButtonText
            : texts.makeSuggestionLabelActionDisabled,
          disabled: true,
          type: "primary",
        },
      });
    }

    return (
      <section
        className={cx("c-suggestions-view-performer", {
          "c-suggestions-view-performer--print": isPrintView,
        })}
      >
        {isEditMode || isLoading
          ? this.renderEditSection()
          : this.renderPerformer()}
      </section>
    );
  }

  public renderEditSection = () => {
    const { selectedAgentId, isLoading } = this.state;
    const { texts, renderButtons, renderTitle, onNudge } = this.props;

    const buttonProps: IViewPerformerButtonProps = {
      primary: {
        text: texts.makeSuggestionActionNudge,
        onClick: () => onNudge(selectedAgentId),
        type: "primary",
      },
    };

    return (
      <>
        <div className="c-suggestions-view-performer__content g-bg-default">
          {renderTitle()}
          <div className="g-inner-grid-12">
            <div className="g-col-lg-6">
              {isLoading
                ? this.renderLoadingStub()
                : this.renderAgencySection()}
            </div>
          </div>
        </div>
        {renderButtons(buttonProps)}
      </>
    );
  };

  private renderAgencySection = () => {
    const { agencies, selectedAgentId } = this.state;
    const { texts } = this.props;

    if (agencies.length > 1) {
      return (
        <DropdownInput<string>
          id="agencyDropdown"
          className="c-suggestions-view-performer__nudge-access-select"
          label={texts.makeSuggestionLabelNudge}
          variant="select"
          value={selectedAgentId}
          options={this.getAgencyOptions()}
          optionSelected={this.handleAgencyDropdownChange}
        />
      );
    }

    const text = getNudgeAgencyText(
      texts.makeSuggestionLabelNudgeAgent,
      agencies[0]
    );

    return (
      <p
        className="c-suggestions-view-performer__read-access"
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );
  };

  private renderLoadingStub = () => (
    <div className="c-suggestions-view-performer__stub-text">
      <Shimmer />
    </div>
  );

  public renderPerformer() {
    const { texts, isRoleClosed, renderButtons, renderTitle, nudge } =
      this.props;
    const text = prepareText(
      texts.makeSuggestionLabelNudgeDetails,
      nudge!.agency,
      nudge!.dateCreated
    );

    const buttonProps: IViewPerformerButtonProps = isRoleClosed
      ? {
          primary: {
            text: texts.makeSuggestionLabelActionDisabled,
            disabled: true,
            type: "primary",
          },
        }
      : {
          primary: {
            text: texts.makeSuggestionActionNudge,
            disabled: true,
            type: "primary",
          },
        };

    return (
      <>
        <div className="c-suggestions-view-performer__content g-bg-default">
          {renderTitle()}
          <p
            className="c-suggestions-view-performer__read-access"
            dangerouslySetInnerHTML={{ __html: text }}
          />
        </div>
        {renderButtons(buttonProps)}
      </>
    );
  }

  private getAgencyList = async (): Promise<void> => {
    const { getAgencies, handleError } = this.props;

    if (this.state.agencies.length > 0) {
      return this.setState({ isEditMode: true });
    }

    this.setState({ isLoading: true });
    try {
      let agencies = await getAgencies(this.requestCancellation);
      agencies = agencies.filter(
        (item) =>
          (item.nudgeAllowed || item.allowNudge) &&
          !item.suggestAllowed &&
          !item.allowSelfSuggest &&
          item.id !== DEFAULT_CLIENT_AGENCY_ID &&
          item.id !== NO_CLIENT_AGENCY_ID &&
          item.agencyId !== DEFAULT_CLIENT_AGENCY_ID &&
          item.agencyId !== NO_CLIENT_AGENCY_ID
      );

      const selectedAgentId =
        agencies[0]?.agencyId !== undefined
          ? agencies[0]?.agencyId.toString()
          : agencies[0]?.id.toString();

      this.setState({
        selectedAgentId,
        agencies,
        isEditMode: true,
        isLoading: false,
      });
    } catch (e) {
      handleError(e);

      this.setState({ isEditMode: true, isLoading: false });
    }
  };

  private handleAgencyDropdownChange = (option: IOption<string>) => {
    this.setState({ selectedAgentId: option.value });
  };

  private getAgencyOptions = (): IOption<string>[] =>
    this.state.agencies.map((item) => getAgencyOption(item));
}
